import { createHash } from "node:crypto"
import * as errors from "@superbuilders/errors"
import * as logger from "@superbuilders/slog"

import { sanitizeHtml } from "../../html"
import { parseAssessmentItemXml } from "../../parser"
import type {
	DisplayAssociableChoice,
	DisplayBlock,
	DisplayChoice,
	DisplayGap,
	DisplayGapText,
	DisplayInlineEmbed,
	DisplayItem,
	DisplayTextEmbed,
	FormShape
} from "../../shared/types"

function shuffleArray<T>(items: T[]): T[] {
	// Fisher–Yates shuffle on a shallow copy to keep original immutable
	const arr = items.slice()
	for (let i = arr.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1))
		const vi = arr[i]
		const vj = arr[j]
		// With noUncheckedIndexedAccess enabled, index access is possibly undefined.
		// These indices are guaranteed in-bounds by construction; guard to satisfy TS.
		if (vi === undefined || vj === undefined) {
			continue
		}
		arr[i] = vj
		arr[j] = vi
	}
	return arr
}

export function buildDisplayModelFromXml(qtiXml: string): { itemKey: string; item: DisplayItem; shape: FormShape } {
	logger.debug("qti build display model", {})
	const parseResult = errors.trySync(() => parseAssessmentItemXml(qtiXml))
	if (parseResult.error) {
		logger.error("qti parse failed", { error: parseResult.error })
		throw errors.wrap(parseResult.error, "qti parse")
	}

	const item = parseResult.data
	const shape: FormShape = {}
	for (const rd of item.responseDeclarations) {
		// Check baseType first for directedPair (gap match uses this)
		if (rd.baseType === "directedPair" || rd.baseType === "pair") {
			shape[rd.identifier] = "directedPair"
		} else if (rd.cardinality === "single") {
			shape[rd.identifier] = "single"
		} else if (rd.cardinality === "ordered") {
			shape[rd.identifier] = "ordered"
		} else {
			shape[rd.identifier] = "multiple"
		}
	}

	const contentBlocks: DisplayBlock[] = item.itemBody.contentBlocks.map((block) => {
		if (block.type === "stimulus") {
			return { type: "stimulus", html: sanitizeHtml(block.html) }
		}
		if (block.type === "richStimulus") {
			const html = sanitizeHtml(block.html)
			const inlineEmbeds: Record<string, DisplayInlineEmbed> = {}
			for (const [key, embed] of Object.entries(block.inlineEmbeds)) {
				inlineEmbeds[key] = {
					responseId: embed.responseIdentifier,
					shuffle: embed.shuffle,
					choices: embed.choices.map((c) => ({
						id: c.identifier,
						contentHtml: sanitizeHtml(c.contentHtml)
					}))
				}
			}
			const textEmbeds: Record<string, DisplayTextEmbed> = {}
			for (const [key, embed] of Object.entries(block.textEmbeds)) {
				textEmbeds[key] = {
					responseId: embed.responseIdentifier,
					expectedLength: embed.expectedLength,
					placeholder: embed.placeholderText,
					patternMask: embed.patternMask
				}
			}
			return { type: "richStimulus", html, inlineEmbeds, textEmbeds }
		}
		const interaction = block.interaction
		if (interaction.type === "choiceInteraction") {
			const promptHtml = sanitizeHtml(interaction.promptHtml)
			let choices: DisplayChoice[] = interaction.choices.map((c) => ({
				id: c.identifier,
				contentHtml: sanitizeHtml(c.contentHtml),
				inlineFeedbackHtml: c.inlineFeedbackHtml ? sanitizeHtml(c.inlineFeedbackHtml) : undefined
			}))
			if (interaction.shuffle === true) {
				choices = shuffleArray(choices)
			}
			// Choice interactions only use "single" or "multiple" cardinality
			const cardinality = shape[interaction.responseIdentifier] === "multiple" ? "multiple" : "single"
			return {
				type: "interaction",
				interaction: {
					type: "choice",
					responseId: interaction.responseIdentifier,
					cardinality,
					promptHtml,
					choices,
					minChoices: interaction.minChoices,
					maxChoices: interaction.maxChoices
				}
			}
		}
		if (interaction.type === "textEntryInteraction") {
			return {
				type: "interaction",
				interaction: {
					type: "text",
					responseId: interaction.responseIdentifier,
					expectedLength: interaction.expectedLength,
					placeholder: interaction.placeholderText,
					patternMask: interaction.patternMask
				}
			}
		}
		if (interaction.type === "orderInteraction") {
			let choices: DisplayChoice[] = interaction.choices.map((c) => ({
				id: c.identifier,
				contentHtml: sanitizeHtml(c.contentHtml),
				inlineFeedbackHtml: undefined
			}))

			// Server-side shuffle for initial state
			if (interaction.shuffle === true) {
				choices = shuffleArray(choices)
			}

			return {
				type: "interaction",
				interaction: {
					type: "order",
					responseId: interaction.responseIdentifier,
					promptHtml: sanitizeHtml(interaction.promptHtml),
					orientation: interaction.orientation,
					choices
				}
			}
		}
		if (interaction.type === "gapMatchInteraction") {
			let gapTexts: DisplayGapText[] = interaction.gapTexts.map((gt) => ({
				id: gt.identifier,
				contentHtml: sanitizeHtml(gt.contentHtml),
				matchMax: gt.matchMax
			}))

			// Server-side shuffle for source tokens
			if (interaction.shuffle === true) {
				gapTexts = shuffleArray(gapTexts)
			}

			const gaps: DisplayGap[] = interaction.gaps.map((g) => ({
				id: g.identifier
			}))

			return {
				type: "interaction",
				interaction: {
					type: "gapMatch",
					responseId: interaction.responseIdentifier,
					gapTexts,
					gaps,
					contentHtml: sanitizeHtml(interaction.contentHtml)
				}
			}
		}
		if (interaction.type === "matchInteraction") {
			const mapChoice = (c: {
				identifier: string
				matchMax: number
				contentHtml: string
			}): DisplayAssociableChoice => ({
				id: c.identifier,
				contentHtml: sanitizeHtml(c.contentHtml),
				matchMax: c.matchMax
			})

			let sourceChoices = interaction.sourceChoices.map(mapChoice)
			const targetChoices = interaction.targetChoices.map(mapChoice)

			// Server-side shuffle for sources (targets stay fixed for consistency)
			if (interaction.shuffle === true) {
				sourceChoices = shuffleArray(sourceChoices)
			}

			return {
				type: "interaction",
				interaction: {
					type: "match",
					responseId: interaction.responseIdentifier,
					promptHtml: sanitizeHtml(interaction.promptHtml),
					maxAssociations: interaction.maxAssociations,
					sourceChoices,
					targetChoices
				}
			}
		}
		// Fallback: unknown interactions emit a stimulus note
		return { type: "stimulus", html: sanitizeHtml("") }
	})

	const displayItem: DisplayItem = { title: item.title, contentBlocks }
	const hash = createHash("sha256").update(qtiXml).digest("hex").slice(0, 16)

	return { itemKey: hash, item: displayItem, shape }
}
